import { FC } from "react";
import TableHeaderComponent from "./table-header";
import { useUrlState } from "../../utils";
import TableBodyComponent from "./table-body";
import { TableColumn, TColumn } from "../../utils/types";
import TableGridBodyComponent from "./table-grid-body";
import { TableEmpty } from "./table-empty";
import { Show } from "components/core";

interface TableComponentProps<TData = any> {
  title: string;
  columns: TableColumn<TColumn<TData>>;
  data: (TData | null)[];
  loading?: boolean;
  isRefetching?: boolean;
  renderColumns?: FC<TData>;
  renderItem?: FC<TData>;
  renderGridItem?: FC<{ item: TData; index: number }>;
  renderFilter?: FC<{
    filterOpen: boolean;
    setFilterOpen: (val: boolean) => void;
  }>;
  renderExport?: FC<{
    exportOpen: boolean;
    setExportOpen: (val: boolean) => void;
  }>;
  renderLoader?: FC;
  renderGridLoader?: FC;
  renderHeaderItems?: FC;
  renderActions?: FC;
  refetch: () => void;
  hasSearch?: boolean;
  defaultView?: "grid" | "list";
  pagination?: FC;
  showHeader?: boolean;
  emptyMessage?: string;
  viewType?: "grid" | "list";
}

const Table = <TData,>({
  title,
  renderFilter,
  renderExport,
  renderGridItem,
  renderItem,
  refetch,
  renderHeaderItems,
  renderActions,
  hasSearch,
  isRefetching,
  defaultView = "list",
  viewType: _viewType,
  data,
  loading,
  renderLoader,
  renderGridLoader,
  columns,
  pagination,
  showHeader = true,
  emptyMessage,
}: TableComponentProps<TData>) => {
  const [urlViewType] = useUrlState("viewType");
  const viewType = _viewType ?? urlViewType ?? defaultView;
  const hasGridMode = !!renderGridItem;
  const isEmpty = !loading && (data?.length || 0) === 0;
  return (
    <div className="h-full flex flex-col overflow-hidden w-full">
      <div className="px-4 sm:px-6 flex-shrink-0 w-full">
        <TableHeaderComponent
          title={title}
          renderFilter={renderFilter}
          renderExport={renderExport}
          gridable={hasGridMode}
          listable={_viewType === undefined}
          refetch={refetch}
          renderHeaderItems={renderHeaderItems}
          renderActions={renderActions}
          hasSearch={hasSearch}
          loading={isRefetching}
          defaultView={defaultView}
          hasPagination={Boolean(pagination)}
        />
      </div>

      <div className="flex-1 min-h-0 relative my-2 w-full overflow-auto">
        <Show if={!isEmpty}>
          <Show if={viewType === "grid" || _viewType === "grid"}>
            <div className="h-full w-full px-4">
              <TableGridBodyComponent
                data={data}
                loading={loading}
                renderLoader={renderGridLoader}
                renderItem={renderGridItem}
              />
            </div>
          </Show>
          <Show if={viewType === "list" || _viewType === "list"}>
            <TableBodyComponent
              data={data}
              loading={loading}
              renderLoader={renderLoader}
              renderItem={renderItem}
              columns={columns}
              isEmpty={isEmpty}
              showHeader={showHeader}
            />
          </Show>
        </Show>
        <Show if={isEmpty}>
          <TableEmpty message={emptyMessage || "No data available"} />
        </Show>
      </div>

      <div className="px-4 sm:px-6 flex-shrink-0">{pagination?.({})}</div>
    </div>
  );
};

export function createTable<T>() {
  return Table<T>;
}

export default Table;
